import { useCallback, useEffect, useRef, useState } from "react";

export interface IUpdateStateAfterSearchParams {
  fetchSide: 'afterLast' | 'beforeFirst' | 'replace';
  newFirstElementId: number | undefined;
  newLastElementId: number | undefined;
  elementsFetched: number;
}

export interface IGetPageRequestParams {
  afterId?: number;
  beforeId?: number;
  limit?: number;
  sortColumn?: string;
  sortOrder?: string;
  fetchMode: 'append' | 'replace',
}

export default function usePagingAndSort(
  getData: (
    params: IGetPageRequestParams,
    signal: AbortSignal,
    updateStateAfterFetch: (updateStateParams: IUpdateStateAfterSearchParams) => void
  ) => void,
  limit: number,
) {
  const [lastElementId, setLastElementId] = useState<number | undefined>(undefined);
  const [firstElementId, setFirstElementId] = useState<number | undefined>(undefined);
  const [loadingAfter, setLoadingAfter] = useState(true);
  const [hasMoreAfter, setHasMoreAfter] = useState(false);
  const [loadingBefore, setLoadingBefore] = useState(false);
  const [hasMoreBefore, setHasMoreBefore] = useState(true);
  const [prevSortColumn, setPrevSortColumn] = useState<string | undefined>(undefined);
  const [prevSortOrder, setPrevSortOrder] = useState<'ASC' | 'DESC' | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC' | undefined>(undefined);
  const [error, setError] = useState(false);

  const updateStateAfterFetch = useCallback((
    updateStateParams: IUpdateStateAfterSearchParams
  ) => {
    if (updateStateParams.newLastElementId !== undefined) {
      setLastElementId(updateStateParams.newLastElementId);
    }
    if (updateStateParams.newFirstElementId !== undefined) {
      setFirstElementId(updateStateParams.newFirstElementId);
    }
    if (updateStateParams.fetchSide === 'afterLast' || updateStateParams.fetchSide === 'replace') {
      setHasMoreAfter(updateStateParams.elementsFetched >= limit);
      setLoadingAfter(false);
    }
    if (updateStateParams.fetchSide === 'beforeFirst' || updateStateParams.fetchSide === 'replace') {
      setHasMoreBefore(updateStateParams.elementsFetched >= limit);
      setLoadingBefore(false);
    }
  }, [limit]);

  const lastTableElementDisplayedObserver = useRef<IntersectionObserver>();

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (e.currentTarget.scrollTop === 0 && hasMoreBefore) {
      if (loadingBefore) return;
      setLoadingBefore(true);
    }
  };

  const lastElementRef = useCallback(
    (node: any) => {
      if (loadingAfter || !hasMoreAfter) return;
      if (lastTableElementDisplayedObserver.current) lastTableElementDisplayedObserver.current.disconnect();
      lastTableElementDisplayedObserver.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMoreAfter) {
          setLoadingAfter(true);
        }
      });
      if (node) lastTableElementDisplayedObserver.current.observe(node);
    },
    [loadingAfter, hasMoreAfter]
  );

  useEffect(() => {
    const abortCtrl = new AbortController();

    const fetchData =
      (prevSortColumn !== sortColumn || prevSortOrder !== sortOrder || (lastElementId === undefined && firstElementId === undefined))
        ? async () => {
          try {
            setPrevSortColumn(sortColumn);
            setPrevSortOrder(sortOrder);
            setError(false);
            getData({ limit, sortColumn, sortOrder, fetchMode: 'replace' }, abortCtrl.signal, updateStateAfterFetch);
          } catch (err) {
            setError(true);
          }
        }
        : (loadingAfter && lastElementId)
          ? async () => {
            try {
              setError(false);
              getData({ limit, afterId: lastElementId, sortColumn, sortOrder, fetchMode: 'append' }, abortCtrl.signal, updateStateAfterFetch);
            } catch (err) {
              setError(true);
            }
          }
          : (loadingBefore && firstElementId)
            ? async () => {
              try {
                setError(false);
                getData({ limit, beforeId: firstElementId, sortColumn, sortOrder, fetchMode: 'append' }, abortCtrl.signal, updateStateAfterFetch);
              } catch (err) {
                setError(true);
              }
            }
            : undefined;

    if (fetchData) {
      fetchData();
    }

    return () => {
      if (fetchData) {
        abortCtrl.abort();
      }
    };
  }, [getData, limit, sortColumn, sortOrder, lastElementId, loadingAfter, firstElementId, loadingBefore, prevSortColumn, prevSortOrder, updateStateAfterFetch]);

  const doSortBy = useCallback((newSortColumn: string) => {
    if (sortColumn === newSortColumn) {
      if (sortOrder === 'ASC') {
        setSortOrder('DESC');
      } else {
        setSortOrder('ASC');
      }
    } else {
      setSortColumn(newSortColumn);
      setSortOrder('ASC');
    }
  }, [sortColumn, sortOrder, setSortColumn, setSortOrder]);

  const resetAfterSearch = useCallback(() => {
    setLoadingAfter(true);
    setFirstElementId(undefined);
    setLastElementId(undefined);
  }, []);

  return {
    loadingAfter,
    loadingBefore,
    doSortBy,
    resetAfterSearch,
    sortColumn,
    sortOrder,
    handleScroll,
    lastElementRef,
    error,
  };
}
